import React, { useState, useMemo, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, LayersControl, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import api from '../services/api';
import './ParkingCarteView.css';

const DEFAULT_CENTER = [14.6415, -61.0242];
const DEFAULT_ZOOM   = 12;
const MAX_ZOOM       = 21;   // zoom max d'affichage
const MAX_NATIVE     = 19;   // dernier niveau de tuiles réel ; au-delà, Leaflet agrandit (pas de gris)

// Orthophoto IGN (Géoplateforme) — gratuit, sans clé, haute résolution sur les territoires
// français (DOM inclus). Vraie imagerie jusqu'au z19 en Martinique ; au-delà → 404 (Leaflet
// agrandit la dernière tuile au lieu d'afficher le placeholder « Map data not yet available »).
const IGN_ORTHO_URL =
  'https://data.geopf.fr/wmts?SERVICE=WMTS&REQUEST=GetTile&VERSION=1.0.0' +
  '&LAYER=ORTHOIMAGERY.ORTHOPHOTOS&STYLE=normal&TILEMATRIXSET=PM&FORMAT=image/jpeg' +
  '&TILEMATRIX={z}&TILEROW={y}&TILECOL={x}';

/* ── Icône SVG colorée (réutilisée comme icône Leaflet) ─────────────── */
const iconCache = {};
function svgIcon(color) {
  if (iconCache[color]) return iconCache[color];
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="38" viewBox="0 0 32 38">
    <filter id="sh"><feDropShadow dx="0" dy="2" stdDeviation="2" flood-color="rgba(0,0,0,0.35)"/></filter>
    <path d="M16 0C7.163 0 0 7.163 0 16c0 10 16 22 16 22S32 26 32 16C32 7.163 24.837 0 16 0z"
          fill="${color}" filter="url(#sh)"/>
    <circle cx="16" cy="16" r="8" fill="white" opacity="0.9"/>
  </svg>`;
  const icon = L.icon({
    iconUrl: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
    iconSize:   [32, 38],
    iconAnchor: [16, 38],
    popupAnchor: [0, -34],
  });
  iconCache[color] = icon;
  return icon;
}

/* Ajuste la vue aux marqueurs — une seule fois (au montage), vue stable ensuite */
function FitBounds({ points }) {
  const map = useMap();
  const done = useRef(false);
  useEffect(() => {
    if (done.current || !points.length) return;
    if (points.length > 1) {
      map.fitBounds(points, { padding: [48, 48] });
    } else {
      map.setView(points[0], 17);
    }
    done.current = true;
  }, [points, map]);
  return null;
}

/* ═══════════════════════════════════════════════════════════════════════
   Composant principal
═══════════════════════════════════════════════════════════════════════ */
export default function ParkingCarteView({ entries, statuts, onOpenEntry }) {
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [popupPhoto, setPopupPhoto]       = useState(null);
  const [photoLoading, setPhotoLoading]   = useState(false);

  /* Charge la photo dès qu'un marqueur est sélectionné */
  useEffect(() => {
    if (!selectedEntry || !selectedEntry.has_photo) { setPopupPhoto(null); return; }
    let cancelled = false;
    setPhotoLoading(true);
    setPopupPhoto(null);
    api.get(`/parking/${selectedEntry.id}/photo`)
      .then(res => { if (!cancelled) setPopupPhoto(res.data.image_data); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setPhotoLoading(false); });
    return () => { cancelled = true; };
  }, [selectedEntry]);

  const statutColorMap = useMemo(() => {
    const m = {};
    (statuts || []).forEach(s => { m[s.valeur] = s.couleur || '#64748b'; });
    return m;
  }, [statuts]);

  const geoEntries = useMemo(
    () => entries.filter(e =>
      e.latitude != null && e.longitude != null &&
      !isNaN(Number(e.latitude)) && !isNaN(Number(e.longitude))
    ),
    [entries]
  );

  const points = useMemo(
    () => geoEntries.map(e => [Number(e.latitude), Number(e.longitude)]),
    [geoEntries]
  );

  const totalFiltres = entries.length;
  const totalGeo     = geoEntries.length;
  const totalSansGeo = totalFiltres - totalGeo;

  return (
    <div className="pcv-wrapper">

      {/* ── Bandeau info / légende ── */}
      <div className="pcv-infobar">
        <span className="pcv-count">
          📍 <strong>{totalGeo}</strong> véhicule{totalGeo !== 1 ? 's' : ''} géolocalisé{totalGeo !== 1 ? 's' : ''}
          {totalSansGeo > 0 && (
            <span className="pcv-nogeo"> · {totalSansGeo} sans coordonnées GPS</span>
          )}
        </span>
        {statuts && statuts.length > 0 && (
          <div className="pcv-legend">
            {statuts.map(s => (
              <span key={s.valeur} className="pcv-legend-item">
                <span className="pcv-legend-dot" style={{ background: s.couleur || '#64748b' }} />
                {s.nom}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* ── Carte ── */}
      <div className="pcv-map-container">
        {geoEntries.length === 0 ? (
          <div className="pcv-empty">
            <div style={{ fontSize: 48, marginBottom: 12 }}>🗺️</div>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>Aucun véhicule géolocalisé</div>
            <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>
              {totalFiltres === 0
                ? 'Aucun véhicule ne correspond aux filtres actifs.'
                : `${totalFiltres} véhicule${totalFiltres !== 1 ? 's' : ''} filtré${totalFiltres !== 1 ? 's' : ''}, mais aucun n'a de coordonnées GPS enregistrées.`}
            </div>
          </div>
        ) : (
          <MapContainer
            className="pcv-gmap"
            center={DEFAULT_CENTER}
            zoom={DEFAULT_ZOOM}
            maxZoom={MAX_ZOOM}
            scrollWheelZoom
            zoomControl
          >
            <LayersControl position="topright">
              <LayersControl.BaseLayer checked name="Satellite (IGN)">
                <TileLayer
                  url={IGN_ORTHO_URL}
                  attribution='Orthophotos &copy; IGN / Géoplateforme'
                  maxZoom={MAX_ZOOM}
                  maxNativeZoom={MAX_NATIVE}
                />
              </LayersControl.BaseLayer>
              <LayersControl.BaseLayer name="Plan (OpenStreetMap)">
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; contributeurs OpenStreetMap'
                  maxZoom={MAX_ZOOM}
                  maxNativeZoom={MAX_NATIVE}
                />
              </LayersControl.BaseLayer>
            </LayersControl>

            <FitBounds points={points} />

            {geoEntries.map(entry => {
              const color = statutColorMap[entry.statut] || '#64748b';
              return (
                <Marker
                  key={entry.id}
                  position={[Number(entry.latitude), Number(entry.longitude)]}
                  icon={svgIcon(color)}
                  title={entry.identifiant || ''}
                  eventHandlers={{
                    click: () => setSelectedEntry(entry),
                    popupclose: () => setSelectedEntry(null),
                  }}
                >
                  <Popup>
                    <div className="pcv-popup-inner">
                      {entry.has_photo && selectedEntry?.id === entry.id && (
                        <div className="pcv-popup-photo">
                          {photoLoading && <div className="pcv-popup-photo-loader">⏳</div>}
                          {popupPhoto && (
                            <img
                              src={`data:image/jpeg;base64,${popupPhoto}`}
                              alt="véhicule"
                              className="pcv-popup-photo-img"
                            />
                          )}
                        </div>
                      )}

                      <div className="pcv-popup-header">
                        <span className="plaque-badge" style={{ fontSize: 14 }}>
                          {entry.identifiant || '—'}
                        </span>
                        {entry.marque && <span className="pcv-popup-marque">{entry.marque}</span>}
                      </div>

                      {entry.allee && (
                        <div className="pcv-popup-row">📍 <span>{entry.allee}</span></div>
                      )}

                      {entry.statut && (
                        <div className="pcv-popup-row">
                          <span
                            className="pcv-popup-statut"
                            style={{
                              background:  (statutColorMap[entry.statut] || '#64748b') + '22',
                              color:        statutColorMap[entry.statut] || '#64748b',
                              borderColor: (statutColorMap[entry.statut] || '#64748b') + '55',
                            }}
                          >
                            {statuts.find(s => s.valeur === entry.statut)?.nom || entry.statut}
                          </span>
                        </div>
                      )}

                      {entry.concession && (
                        <div className="pcv-popup-row pcv-popup-concession">🏢 {entry.concession}</div>
                      )}

                      <button
                        className="pcv-popup-btn"
                        onClick={() => { onOpenEntry(entry); setSelectedEntry(null); }}
                      >
                        ✏️ Voir la fiche
                      </button>
                    </div>
                  </Popup>
                </Marker>
              );
            })}
          </MapContainer>
        )}
      </div>
    </div>
  );
}
